import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { DashboardTileDto } from '@api/models/dashboard/DashboardTileDto';
import { ExplorationConfig, ExplorationStateSnapshot } from './useAnalyticsExploration';
import { AnalyticsDomainConfig } from './analyticsDomainConfig';
import { DOMAIN_CONFIG_MAP } from './analyticsDomainRegistry';
import { useAnalyticsSearchParams } from './useAnalyticsSearchParams';
import { useSidebarOverride } from './useSidebarOverride';

export type AnalyticsTab = 'overview' | 'explorer';

interface UseExplorerNavigationResult {
  activeTab: AnalyticsTab;
  explorerConfig: ExplorationConfig | undefined;
  handleExploreTile: (tile: DashboardTileDto) => void;
  handleTabChange: (_: SyntheticEvent, tab: AnalyticsTab) => void;
  handleBackToOverview: () => void;
  handleStateChange: (state: ExplorationStateSnapshot) => void;
  restoreSidebar: () => void;
}

/**
 * Manages explorer tab state, URL hydration, sidebar visibility,
 * and all navigation callbacks for the analytics page.
 */
export const useExplorerNavigation = (selectedDomain: AnalyticsDomainConfig | undefined): UseExplorerNavigationResult => {
  const { navigate } = useNavigation();
  const reactRouterNavigate = useNavigate();
  const location = useLocation();
  const { hideSidebar, restoreSidebar } = useSidebarOverride();
  const { syncToUrl } = useAnalyticsSearchParams();

  const activeTab: AnalyticsTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') === 'explorer' ? 'explorer' : 'overview';
  }, [location.search]);

  const [explorerConfig, setExplorerConfig] = useState<ExplorationConfig | undefined>(undefined);

  const handleExploreTile = useCallback(
    (tile: DashboardTileDto) => {
      const domainConfig = DOMAIN_CONFIG_MAP[tile.domain];
      if (!domainConfig) return;

      const formValues = domainConfig.fromTileFilters(tile.filters ?? {});
      const groupBy = tile.groupBy ?? domainConfig.defaultGroupBy;
      const config: ExplorationConfig = {
        groupBy,
        formValues,
        tileId: tile.id,
        tileTitle: tile.title ?? undefined,
        tileChartType: tile.chartType,
        tileMetric: tile.metric ?? undefined,
      };

      const domainParams = domainConfig.toUrlParams(formValues);
      const params = new URLSearchParams();
      params.set('tab', 'explorer');
      if (groupBy !== domainConfig.defaultGroupBy) {
        params.set('groupBy', groupBy);
      }
      for (const [key, value] of Object.entries(domainParams)) {
        params.set(key, value);
      }

      setExplorerConfig(config);
      hideSidebar();

      navigate(PathKey.AnalyticsDomain, { domain: domainConfig.key }, Object.fromEntries(params));
    },
    [hideSidebar, navigate],
  );

  const handleTabChange = useCallback(
    (_: SyntheticEvent, tab: AnalyticsTab) => {
      if (tab === 'explorer') {
        hideSidebar();
        const params = new URLSearchParams(location.search);
        params.set('tab', 'explorer');
        reactRouterNavigate({ search: `?${params.toString()}` }, { replace: true });
      } else {
        setExplorerConfig(undefined);
        restoreSidebar();
        reactRouterNavigate({ search: '' }, { replace: true });
      }
    },
    [hideSidebar, restoreSidebar, reactRouterNavigate, location.search],
  );

  const handleStateChange = useCallback(
    (state: ExplorationStateSnapshot) => {
      if (!selectedDomain) return;
      syncToUrl(selectedDomain, state);
    },
    [selectedDomain, syncToUrl],
  );

  const handleBackToOverview = useCallback(() => {
    restoreSidebar();
    reactRouterNavigate({ search: '' }, { replace: true });
  }, [restoreSidebar, reactRouterNavigate]);

  useEffect(() => {
    if (selectedDomain && activeTab === 'explorer') hideSidebar();
    else restoreSidebar();
  }, [selectedDomain, activeTab, hideSidebar, restoreSidebar]);

  return {
    activeTab,
    explorerConfig,
    handleExploreTile,
    handleTabChange,
    handleBackToOverview,
    handleStateChange,
    restoreSidebar,
  };
};
